import api from './api';
import { Lead, LeadStatus } from '../types';

export interface LeadAssignmentInput {
  brand_id?: number | null;
  user_id: number;
  status?: LeadStatus;
}

export interface CreateLeadData {
  party_name: string;
  office_address?: string;
  project_district?: string;
  project_location?: string;
  work_details?: string;
  contact_person?: string;
  secondary_contact_person?: string;
  contact_no: string;
  secondary_contact_no?: string;
  email?: string | null;
  product_req?: string;
  qty_req?: string;
  brands_in_use?: string;
  price?: number;
  source?: string;
  status?: LeadStatus;
  assigned_to?: number;
  assignments: LeadAssignmentInput[];
  conversion_status?: 'not_ready' | 'ready' | 'converted';
  ready_for_conversion?: boolean;
}

export interface UpdateLeadData {
  party_name?: string;
  office_address?: string;
  project_district?: string;
  project_location?: string;
  work_details?: string;
  contact_person?: string;
  secondary_contact_person?: string;
  contact_no?: string;
  secondary_contact_no?: string;
  email?: string | null;
  product_req?: string;
  qty_req?: string;
  brands_in_use?: string;
  price?: number;
  source?: string;
  status?: LeadStatus;
  assigned_to?: number;
  assignments?: LeadAssignmentInput[];
  conversion_status?: 'not_ready' | 'ready' | 'converted';
  ready_for_conversion?: boolean;
  next_call_date?: string;
}

export interface LeadFilters {
  status?: LeadStatus;
  assigned_to?: number;
  brand_id?: number;
  source?: string;
  /** When true, list leads with null/blank source only */
  source_empty?: boolean;
  /** Latest call outcome for the assignment row; `_none` = no outcome / no calls */
  last_call_outcome?: string;
  project_district?: string;
  conversion_status?: 'not_ready' | 'ready' | 'converted';
  ready_for_conversion?: boolean;
  search?: string;
  next_call_due?: 'today' | 'overdue' | 'upcoming';
  call_date_from?: string;
  call_date_to?: string;
  page?: number;
  per_page?: number;
}

export interface LeadListFilterOptions {
  sources: string[];
  call_outcomes: string[];
}

export interface LeadListResponse {
  data: Lead[];
  total: number;
}

export interface BulkActionResponse {
  total_selected: number;
  updated_count: number;
  failed_count: number;
  errors: string[];
}

export const leadService = {
  getFilterOptions: async (): Promise<LeadListFilterOptions> => {
    const response = await api.get('/api/leads/filter-options');
    return response.data;
  },

  getAll: async (filters?: LeadFilters): Promise<LeadListResponse> => {
    const response = await api.get('/api/leads', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<Lead> => {
    const response = await api.get(`/api/leads/${id}`);
    return response.data;
  },

  create: async (data: CreateLeadData): Promise<Lead> => {
    const response = await api.post('/api/leads', data);
    return response.data;
  },

  update: async (id: number, data: UpdateLeadData): Promise<void> => {
    await api.put(`/api/leads/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/leads/${id}`);
  },

  bulkDelete: async (leadIds: number[]): Promise<BulkActionResponse> => {
    const response = await api.post('/api/leads/bulk-delete', { lead_ids: leadIds });
    return response.data;
  },

  bulkAssign: async (
    leadIds: number[],
    assignments: LeadAssignmentInput[]
  ): Promise<BulkActionResponse> => {
    const response = await api.post('/api/leads/bulk-assign', {
      lead_ids: leadIds,
      assignments
    });
    return response.data;
  },

  bulkUnassign: async (
    leadIds: number[],
    assignments: Pick<LeadAssignmentInput, 'brand_id' | 'user_id'>[]
  ): Promise<BulkActionResponse> => {
    const response = await api.post('/api/leads/bulk-unassign', {
      lead_ids: leadIds,
      assignments
    });
    return response.data;
  },

  deleteAssignment: async (leadId: number, assignmentId: number): Promise<void> => {
    await api.delete(`/api/leads/${leadId}/assignments/${assignmentId}`);
  },

  bulkStatus: async (leadIds: number[], status: LeadStatus): Promise<BulkActionResponse> => {
    const response = await api.post('/api/leads/bulk-status', { lead_ids: leadIds, status });
    return response.data;
  },

  bulkSource: async (leadIds: number[], source: string): Promise<BulkActionResponse> => {
    const response = await api.post('/api/leads/bulk-source', { lead_ids: leadIds, source });
    return response.data;
  },

  markReadyForConversion: async (id: number): Promise<void> => {
    await api.post(`/api/leads/${id}/mark-ready`);
  },

  convertToCustomer: async (id: number, data: {
    brand_id?: number;
    status?: string;
    customer_code?: string;
    sap_id?: string;
    work_order_value?: number;
    notes?: string;
    sales_officer_id?: number;
  }): Promise<{ customer_id: number; customer: any }> => {
    const response = await api.post(`/api/leads/${id}/convert-to-customer`, data);
    return response.data;
  },
};

